import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { FileDown, FileSpreadsheet, BadgeCheck, Plus, FileText, Send } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { EmptyState, MonthFilter, PageHeader } from "../../components/Common";
import { TripList } from "../../components/TripList";
import { TripFormDialog } from "../../components/TripFormDialog";
import { ProofDialog } from "../../components/ProofDialog";
import { PayDialog } from "../../components/PayDialog";
import { OrderDialog } from "../../components/OrderDialog";
import { orderMessage, waLink } from "../../lib/order";
import { DriverSelect } from "./AdminPayments";
import { api, cleanParams, currentMonth, downloadFile, formatApiError, formatIDR } from "../../lib/api";

function StatusSelect({ value, onChange }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Status</span>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="h-10 w-40 bg-white" data-testid="status-filter"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Semua Status</SelectItem>
          <SelectItem value="order">Order</SelectItem>
          <SelectItem value="pending">Belum Dibayar</SelectItem>
          <SelectItem value="paid">Lunas</SelectItem>
        </SelectContent>
      </Select>
    </label>
  );
}

export default function AdminReports() {
  const [params] = useSearchParams();
  const [month, setMonth] = useState(params.get("month") ?? currentMonth());
  const [driverId, setDriverId] = useState(params.get("driver") || "all");
  const [status, setStatus] = useState("all");
  const [trips, setTrips] = useState([]);
  const [selected, setSelected] = useState([]);
  const [editing, setEditing] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [proofId, setProofId] = useState(null);
  const [payOpen, setPayOpen] = useState(false);
  const [orderOpen, setOrderOpen] = useState(false);
  const [orderVendor, setOrderVendor] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);
  const [drivers, setDrivers] = useState([]);
  useEffect(() => { api.get("/users").then((r) => setDrivers(r.data)); }, []);

  const filters = { month, driver_id: driverId, status };
  const load = useCallback(async () => {
    const r = await api.get("/trips", { params: cleanParams({ month, driver_id: driverId, status }) });
    setTrips(r.data);
    setSelected([]);
  }, [month, driverId, status]);
  useEffect(() => { load(); }, [load]);

  const selectedTrips = trips.filter((t) => selected.includes(t.id));
  const toggle = (t) => {
    if (!selected.includes(t.id) && selectedTrips.length && selectedTrips[0].driver_id !== t.driver_id)
      return toast.error("Pembayaran hanya bisa untuk satu driver sekaligus");
    setSelected((s) => (s.includes(t.id) ? s.filter((x) => x !== t.id) : [...s, t.id]));
  };
  const onDelete = async (t) => {
    try { await api.delete(`/trips/${t.id}`); toast.success("Trip dihapus"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const exportAs = (fmt) => downloadFile("/export", { ...filters, format: fmt }, `report-driver-${month || "semua"}.${fmt}`).catch((e) => toast.error(formatApiError(e)));
  const total = trips.filter((t) => t.status !== "order").reduce((s, t) => s + t.grand_total, 0);
  const orderCount = trips.filter((t) => t.status === "order").length;
  const openForm = (t) => {
    if (t?.status === "order") { setEditingOrder(t); setOrderVendor(!!drivers.find((d) => d.id === t.driver_id)?.is_vendor); setOrderOpen(true); return; }
    setEditing(t);
    setFormOpen(true);
  };
  const sendWa = (t) => {
    const d = drivers.find((x) => x.id === t.driver_id);
    window.open(waLink(d?.phone, orderMessage(t, !!d?.is_vendor)), "_blank");
  };
  const canSlip = month && driverId !== "all";
  const downloadSlip = () => downloadFile("/slip", { month, driver_id: driverId }, `slip-${month}.pdf`).catch((e) => toast.error(formatApiError(e)));

  return (
    <div>
      <PageHeader eyebrow="Admin" title="Report Trip">
        <MonthFilter value={month} onChange={setMonth} />
        <DriverSelect value={driverId} onChange={setDriverId} />
        <StatusSelect value={status} onChange={setStatus} />
        <Button onClick={() => { setEditingOrder(null); setOrderVendor(false); setOrderOpen(true); }} className="h-10 bg-slate-900 hover:bg-slate-800 font-semibold" data-testid="create-order-btn">
          <Send className="h-4 w-4 mr-1" /> Kirim ke Driver
        </Button>
        <Button onClick={() => { setEditingOrder(null); setOrderVendor(true); setOrderOpen(true); }} className="h-10 bg-violet-600 hover:bg-violet-700 font-semibold" data-testid="create-vendor-order-btn">
          <Send className="h-4 w-4 mr-1" /> Kirim ke Vendor
        </Button>
        <Button onClick={() => openForm(null)} className="h-10 bg-emerald-600 hover:bg-emerald-700 font-semibold" data-testid="admin-add-trip-btn">
          <Plus className="h-4 w-4 mr-1" /> Tambah Trip
        </Button>
      </PageHeader>

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <p className="text-sm text-slate-500">
          <span className="font-num font-semibold text-slate-900" data-testid="reports-count">{trips.length - orderCount}</span> trip{orderCount > 0 && <> · <span className="font-num font-semibold text-sky-700" data-testid="reports-order-count">{orderCount}</span> order</>} · total{" "}
          <span className="font-num font-bold text-emerald-700" data-testid="reports-total">{formatIDR(total)}</span>
        </p>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={downloadSlip} disabled={!canSlip} title={canSlip ? "" : "Pilih bulan dan driver"} data-testid="admin-slip-btn"><FileText className="h-4 w-4 mr-1" />Slip PDF</Button>
          <Button variant="outline" size="sm" onClick={() => exportAs("csv")} data-testid="admin-export-csv-btn"><FileDown className="h-4 w-4 mr-1" />CSV</Button>
          <Button variant="outline" size="sm" onClick={() => exportAs("xlsx")} data-testid="admin-export-xlsx-btn"><FileSpreadsheet className="h-4 w-4 mr-1" />Excel</Button>
        </div>
      </div>

      {trips.length ? (
        <TripList trips={trips} showDriver selectable selected={selected} onToggle={toggle} onEdit={openForm} onDelete={onDelete} onViewProof={setProofId} onSendWa={sendWa} />
      ) : (
        <EmptyState title="Tidak ada trip" text="Ubah filter bulan, driver atau status." testId="empty-reports" />
      )}

      {selected.length > 0 && (
        <div className="fixed bottom-20 lg:bottom-6 left-1/2 -translate-x-1/2 lg:ml-32 z-40 rise flex items-center gap-4 rounded-2xl bg-slate-950 text-white pl-5 pr-2 py-2 shadow-2xl" data-testid="selection-bar">
          <p className="text-sm whitespace-nowrap">{selected.length} trip · <span className="font-num font-bold text-emerald-400">{formatIDR(selectedTrips.reduce((s, t) => s + t.payout, 0))}</span></p>
          <Button onClick={() => setPayOpen(true)} className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold" data-testid="approve-payment-btn">
            <BadgeCheck className="h-4 w-4 mr-1" /> Approve Bayar
          </Button>
        </div>
      )}

      <TripFormDialog open={formOpen} onOpenChange={setFormOpen} trip={editing} onSaved={load} defaultDriverId={driverId === "all" ? "" : driverId} />
      <ProofDialog paymentId={proofId} onClose={() => setProofId(null)} />
      <PayDialog open={payOpen} onOpenChange={setPayOpen} trips={selectedTrips} onPaid={load} />
      <OrderDialog open={orderOpen} onOpenChange={setOrderOpen} order={editingOrder} vendor={orderVendor} defaultDriverId={driverId === "all" ? "" : driverId} onSaved={load} />
    </div>
  );
}
